// LoginForm.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { login } from "../services/authService";
import SocialLoginButtons from "./SocialLoginButtons";
import { FaEnvelope, FaLock } from "react-icons/fa";

const loginStyles = `
  .premium-login-form {
    width: 100%;
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 25px;
  }
 
  .premium-form-group {
    display: flex;
    flex-direction: column;
    position: relative;
  }
 
  .premium-label {
    font-size: 14px;
    font-weight: 700;
    color: #334155;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
 
  .premium-input {
    padding: 15px 18px;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    font-size: 15px;
    color: #0f172a;
    background-color: #ffffff;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
 
  .premium-input:focus {
    outline: none;
    border-color: #3b82f6;
    background-color: #ffffff;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }
 
  .premium-btn {
    padding: 16px;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: #fff;
    border: none;
    border-radius: 14px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.3);
    margin-top: 5px;
  }
 
  .premium-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.4);
  }
 
  .premium-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    filter: grayscale(1);
    box-shadow: none;
  }
 
  .forgot-password-container {
    display: flex;
    justify-content: flex-end;
    margin-top: -15px;
  }
 
  .forgot-password-link {
    font-size: 13px;
    color: #3b82f6;
    text-decoration: none;
    font-weight: 600;
    transition: all 0.2s;
  }
 
  .forgot-password-link:hover {
    color: #2563eb;
    text-decoration: underline;
  }
 
  .divider-container {
    display: flex;
    align-items: center;
    text-align: center;
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 15px 0;
  }
 
  .divider-container::before,
  .divider-container::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e2e8f0;
  }
 
  .divider-container span {
    padding: 0 15px;
  }
`;

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    try {
      await login(email, password); // login service handles localStorage
      toast.success("Login successful! Redirecting...");

      // Get user role from localStorage (set by authService)
      const user = JSON.parse(localStorage.getItem("user"));
      const roleId = user?.role_id;

      setTimeout(() => {
        navigate("/explore");
      }, 1500);
    } catch (err) {
      console.error(err);
      const msg = err.message || "Invalid credentials. Please try again.";
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{loginStyles}</style>
      <form className="premium-login-form" onSubmit={handleSubmit}>
        {errorMessage && (
          <div
            className="error error-message"
            role="alert"
            style={{
              padding: "12px 16px",
              background: "#fee2e2",
              color: "#b91c1c",
              border: "1px solid #fca5a5",
              borderRadius: "8px",
              fontSize: "14px",
              marginBottom: "18px",
              fontWeight: "500",
            }}
          >
            {errorMessage}
          </div>
        )}
        <div className="premium-form-group">
          <label htmlFor="email" className="premium-label"><FaEnvelope color="#3b82f6"/> Email Address</label>
          <input
            className="premium-input"
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
          />
        </div>

        <div className="premium-form-group">
          <label htmlFor="password" className="premium-label"><FaLock color="#3b82f6"/> Password</label>
          <input
            className="premium-input"
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
          />
        </div>

        <div className="forgot-password-container">
          <a href="/reset-password" className="forgot-password-link">Forgot Password?</a>
        </div>

        <button type="submit" className="premium-btn" disabled={isLoading}>
          {isLoading ? "Authenticating..." : "Log In"}
        </button>

        <div className="divider-container">
          <span>or continue with</span>
        </div>

        <SocialLoginButtons />
      </form>
    </>
  );
}

export default LoginForm;
